import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Field, Form, Formik } from "formik";
import { Button, TextBox } from "../../../components";
import { useNavigate, useParams } from "react-router-dom";
import useTendering from "../../../hooks/useTendering";
import useCommon from "../../../hooks/useCommon";
import useQuotation from "../../../hooks/useQuotation";
import {
  dataURLtoFile,
  formatNumber,
  nationalIdValid,
  numberToOrdinal,
} from "../../../helpers/common.helper";
import DocumentUploader from "../../../components/common/DocumentUploader";
import { ReactComponent as TrashSvg } from "../../../assets/admin/icons/trash.svg";
import { ObjectId } from "bson";
import { toast } from "react-toastify";
import { CreateInput } from "thai-address-autocomplete-react";
import SignatureModal from "./SignatureModal";

const InputThaiAddress = CreateInput();

const ContractorContract = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(undefined);
  const { getById, getSignedUrl, signContract } = useTendering();
  const { getUsersAndCompanies } = useCommon();
  const { getQuotation } = useQuotation();
  const [signatureFile, setSignatureFile] = useState(undefined);
  const [signaturePreview, setSignaturePreview] = useState(undefined);
  const [stampFile, setStampFile] = useState(undefined);
  const [stampPreview, setStampPreview] = useState(undefined);
  const [houseRegistrationFile, setHouseRegistrationFile] = useState(undefined);
  const [nationalIdFile, setNationalIdFile] = useState(undefined);
  const [dbdFile, setDbdFile] = useState(undefined);
  const [addr, setAddr] = useState({
    district: undefined,
    amphoe: undefined,
    province: undefined,
    zipcode: undefined,
  });
  const [isDisplaySignatureModal, setIsDisplaySignatureModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const tendering = await getById(id);

    const winner = tendering.participants.find((p) => p.won === true);

    // * Get quotation
    const quotation = await getQuotation(winner.quotation.submissions.at(-1));

    setDetail({
      name: tendering.contract.name,
      owner: tendering.contract.owner.name,
      participant: tendering.contract.contractor.name,
      total: formatNumber(quotation.grandTotal, ""),
      terms: [
        formatNumber(quotation.advancedPayment.total, ""),
        ...quotation.paymentTerms.map((pt) => formatNumber(pt.subTotal, "")),
      ],
      type: "legal",
      business: {
        name: "",
        taxId: "",
      },
      address: {
        streetAddress: "",
        apt: "",
      },
    });
  };

  const handleAddressChange = (scope) => (value) => {
    setAddr((oldAddr) => ({
      ...oldAddr,
      [scope]: value,
    }));
  };

  const handleAddressSelect = (address) => {
    setAddr(address);
  };

  const handleValidate = (values) => {
    const { name, owner, nationalId, type, business, address } = values;

    let errors = {};

    if (!name) errors.name = "Required";
    if (!nationalId) errors.nationalId = "Required";

    if (nationalId && !nationalIdValid(nationalId))
      errors.nationalId = "Invalid national ID number";

    if (address && !address.streetAddress) {
      errors.address = {
        streetAddress: "Required",
      };
    }

    if (!address) {
      errors.address = {
        streetAddress: "Required",
      };
    }

    if (type === "general" && business && !business.name) {
      if (!errors.business) {
        errors.business = {};
      }

      errors.business["name"] = "Required";
    }

    if (type === "general" && business && !business.taxId) {
      if (!errors.business) {
        errors.business = {};
      }

      errors.business["taxId"] = "Required";
    }

    if (type === "general" && !business) {
      errors.business = {
        name: "Required",
        taxId: "Required",
      };
    }

    console.log("errors", errors);

    return errors;
  };

  const _uploadFile = async (key, file) => {
    const signedUrl = await getSignedUrl(key, file.type);

    const uploadResponse = await fetch(signedUrl, {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: 0,
      },
      body: file,
    });

    if (uploadResponse.status !== 200) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const { participant, type, nationalId, address, business } = values;

    if (!signatureFile) {
      toast.error("Signature not found");
      setSubmitting(false);
      return;
    }

    if (!nationalIdFile) {
      toast.error("National ID not found");
      setSubmitting(false);
      return;
    }

    if (!houseRegistrationFile) {
      toast.error("House Registration not found");
      setSubmitting(false);
      return;
    }

    if (type === "general" && !stampFile) {
      toast.error("Stamp not found");
      setSubmitting(false);
      return;
    }

    if (type === "general" && !dbdFile) {
      toast.error("DBD not found");
      setSubmitting(false);
      return;
    }

    // * Upload Signature
    const signatureId = new ObjectId();
    const signatureKey = `tenderings/${id}/contract/contractor/signatures/${signatureId}`;

    if (!(await _uploadFile(signatureKey, signatureFile))) {
      toast.error("Unable to upload signature");
      setSubmitting(false);
      return;
    }

    // * Upload National ID
    const nationalDocId = new ObjectId();
    const nationalDocKey = `tenderings/${id}/contract/contractor/nationalIds/${nationalDocId}`;

    if (!(await _uploadFile(nationalDocKey, nationalIdFile))) {
      toast.error("Unable to upload national ID");
      setSubmitting(false);
      return;
    }

    // * Upload House Registration
    const houseRegistrationId = new ObjectId();
    const houseRegistrationKey = `tenderings/${id}/contract/contractor/nationalIds/${houseRegistrationId}`;

    if (!(await _uploadFile(houseRegistrationKey, houseRegistrationFile))) {
      toast.error("Unable to upload house registration file");
      setSubmitting(false);
      return;
    }

    const _address = {
      ...address,
      subDistrict: addr.district,
      district: addr.amphoe,
      province: addr.province,
      postalCode: addr.zipcode,
    };

    const _body = {
      name: participant,
      type: type,
      nationalId,
      address: _address,
      signature: {
        _id: signatureId,
        key: signatureKey,
        name: signatureFile.name,
        mimeType: signatureFile.type,
        size: signatureFile.size,
      },
      nationalDocument: {
        _id: nationalDocId,
        key: nationalDocKey,
        name: nationalIdFile.name,
        mimeType: nationalIdFile.type,
        size: nationalIdFile.size,
      },
      houseRegistration: {
        _id: houseRegistrationId,
        key: houseRegistrationKey,
        name: houseRegistrationFile.name,
        mimeType: houseRegistrationFile.type,
        size: houseRegistrationFile.size,
      },
    };

    if (type === "general") {
      _body.business = business;

      // * Upload Stamp
      const stampId = new ObjectId();
      const stampKey = `tenderings/${id}/contract/contractor/stamps/${stampId}`;

      if (!(await _uploadFile(stampKey, stampFile))) {
        toast.error("Unable to upload stamp");
        setSubmitting(false);
        return;
      }

      // * Upload DBD
      const dbdId = new ObjectId();
      const dbdKey = `tenderings/${id}/contract/contractor/stamps/${dbdId}`;

      if (!(await _uploadFile(dbdKey, dbdFile))) {
        toast.error("Unable to upload DBD");
        setSubmitting(false);
        return;
      }

      _body.stamp = {
        _id: stampId,
        key: stampKey,
        name: stampFile.name,
        mimeType: stampFile.type,
        size: stampFile.size,
      };

      _body.dbd = {
        _id: dbdId,
        key: dbdKey,
        name: dbdFile.name,
        mimeType: dbdFile.type,
        size: dbdFile.size,
      };
    }

    try {
      await signContract(id, _body);
      toast.success("Contract signed");
      navigate(`/tenderings/${id}`);
    } catch (error) {
      setSubmitting(false);
    }
  };

  if (!detail) return <>Loading</>;

  const { name, owner, participant, nationalId, total, terms } = detail;

  return (
    <Formik
      initialValues={{
        name,
        owner,
        participant,
        nationalId,
        business: {
          name: "",
          taxId: "",
        },
        address: {
          streetAddress: "",
          apt: "",
        },
        total,
        terms,
        type: "legal",
      }}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {({ values }) => (
        <Form className="tendering-contract">
          <h3>Sign Contract</h3>
          <div className="tendering-contract__form">
            <h4>Information</h4>
            <TextBox name="name" label="Project name" disabled />
            <TextBox name="owner" label="Owner's name" disabled />
            <TextBox name="participant" label="Contractor's name" />
            <TextBox name="nationalId" label="National ID" />
            <div className="tendering-contract__form__type">
              <span className="tendering-contract__form__type__title">
                Type
              </span>
              <div className="tendering-contract__form__type__options">
                <label className="tendering-contract__form__type__options__radio">
                  <Field type="radio" name="type" value="legal" />
                  Legal Entity
                </label>
                <label className="tendering-contract__form__type__options__radio">
                  <Field type="radio" name="type" value="general" />
                  General Public
                </label>
              </div>
            </div>
            <div className="tendering-contract__form__separator" />
            {values.type === "general" && (
              <>
                <h4>Business Information</h4>
                <TextBox name="business.name" label="Name" />
                <TextBox name="business.taxId" label="Tax ID" />
              </>
            )}
            {values.type !== "general" && (
              <>
                <h4>Address</h4>
              </>
            )}
            <div className="tendering-contract__form__address">
              <TextBox name="address.streetAddress" label="Street Address" />
              <TextBox name="address.apt" label="Apt, suite, etc. (Optional)" />
              <div className="address-textbox">
                <label className="address-textbox__title">
                  {"Sub District (Thai)"}
                </label>
                <InputThaiAddress.District
                  value={addr["district"]}
                  onChange={handleAddressChange("district")}
                  onSelect={handleAddressSelect}
                  className="address-textbox__input"
                />
              </div>
              <div className="address-textbox">
                <label className="address-textbox__title">
                  {"District (Thai)"}
                </label>
                <InputThaiAddress.Amphoe
                  value={addr["amphoe"]}
                  onChange={handleAddressChange("amphoe")}
                  onSelect={handleAddressSelect}
                  className="address-textbox__input"
                />
              </div>
              <div className="address-textbox">
                <label className="address-textbox__title">
                  {"Province (Thai)"}
                </label>
                <InputThaiAddress.Province
                  value={addr["province"]}
                  onChange={handleAddressChange("province")}
                  onSelect={handleAddressSelect}
                  className="address-textbox__input"
                />
              </div>
              <div className="address-textbox">
                <label className="address-textbox__title">
                  {"Postal Code"}
                </label>
                <InputThaiAddress.Zipcode
                  value={addr["zipcode"]}
                  onChange={handleAddressChange("zipcode")}
                  onSelect={handleAddressSelect}
                  className="address-textbox__input"
                />
              </div>
            </div>
            <div className="tendering-contract__form__separator" />
            <h4>Payment Terms</h4>
            {detail.terms.map((_t, index) => {
              let displayName = "Advanced";

              if (index > 0) {
                displayName = numberToOrdinal(index);
              }

              if (detail.terms.length === index + 1) {
                displayName = "Final";
              }

              return (
                <TextBox
                  name={`terms[${index}]`}
                  label={`${displayName} payment`}
                  disabled={true}
                />
              );
            })}
            <TextBox
              name="total"
              label="Payment Total (Include VAT and/or WHT)"
              disabled={true}
            />
            <div className="tendering-contract__form__separator" />
            <h4>Signature & Documents</h4>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h5>Signature</h5>
              <Button
                label="Create a signature"
                variant={"secondary"}
                size="small"
                onClick={() => setIsDisplaySignatureModal(true)}
              />
            </div>
            <DocumentUploader
              name="signature"
              accept="image/*"
              multiple={false}
              onChange={(files) => {
                if (!files || files.length === 0) return;

                setSignatureFile(files.at(-1));

                const fr = new FileReader();
                fr.onload = function () {
                  setSignaturePreview(fr.result);
                };
                fr.readAsDataURL(files.at(-1));
              }}
            />
            {signaturePreview && (
              <div className="tendering-contract__form__image-preview">
                <img
                  className="tendering-contract__form__image-preview__image"
                  src={signaturePreview}
                  height="120px"
                />
                <TrashSvg
                  className="tendering-contract__form__image-preview__delete"
                  width={16}
                  height={16}
                  onClick={() => {
                    setSignatureFile(undefined);
                    setSignaturePreview(undefined);
                  }}
                />
              </div>
            )}
            {values.type === "general" && (
              <>
                <h5>Company Stamp</h5>
                <DocumentUploader
                  name="stamp"
                  accept="image/*"
                  onChange={(files) => {
                    if (!files || files.length === 0) return;

                    setStampFile(files.at(-1));

                    const fr = new FileReader();
                    fr.onload = function () {
                      setStampPreview(fr.result);
                    };
                    fr.readAsDataURL(files.at(-1));
                  }}
                />
                {stampPreview && (
                  <div className="tendering-contract__form__image-preview">
                    <img
                      className="tendering-contract__form__image-preview__image"
                      src={stampPreview}
                      height="120px"
                    />
                    <TrashSvg
                      className="tendering-contract__form__image-preview__delete"
                      width={16}
                      height={16}
                      onClick={() => {
                        setStampFile(undefined);
                        setStampPreview(undefined);
                      }}
                    />
                  </div>
                )}
                <h5>DBD Document</h5>
                <DocumentUploader
                  name="dbd"
                  onChange={(files) => {
                    if (!files || files.length === 0) return;

                    setDbdFile(files.at(-1));
                  }}
                />
              </>
            )}
            <h5>National ID</h5>
            <DocumentUploader
              name="nationalIdFile"
              onChange={(files) => {
                if (!files || files.length === 0) return;

                setNationalIdFile(files.at(-1));
              }}
            />
            <h5>House Registration</h5>
            <DocumentUploader
              name="houseRegistration"
              onChange={(files) => {
                if (!files || files.length === 0) return;

                setHouseRegistrationFile(files.at(-1));
              }}
            />
          </div>
          <div className="tendering-contract__footer">
            <Button label="Cancel" variant={"secondary"} />
            <Button label="Sign Contract" type="submit" />
          </div>
          {isDisplaySignatureModal && (
            <SignatureModal
              onSave={(data) => {
                const file = dataURLtoFile(data, "signature.png");

                setSignatureFile(file);
                setSignaturePreview(data);
                setIsDisplaySignatureModal(false);
              }}
              onClose={() => {
                setIsDisplaySignatureModal(false);
              }}
            />
          )}
        </Form>
      )}
    </Formik>
  );
};

ContractorContract.propTypes = {};

export default ContractorContract;
